export const PERF_LOW_FPS_THRESHOLD = 45;
export const PERF_LOW_FPS_MIN_DURATION_MS = 3000;
export const PERF_LOW_FPS_COOLDOWN_MS = 60000;
export const DISPLAY_HZ_SAMPLE_MS = 2000;
export const PERF_ROLLING_WINDOW_MS = 1000;
export const PERF_MAX_FRAME_MS = 100;

/**
 * @param {number} cssWidth
 * @param {number} cssHeight
 * @returns {"sm" | "md" | "lg"}
 */
export function getViewportBucket(cssWidth, cssHeight) {
  var w = Math.max(0, Math.floor(Number(cssWidth) || 0));
  var h = Math.max(0, Math.floor(Number(cssHeight) || 0));
  var area = w * h;
  if (area < 500000) return "sm";
  if (area < 1000000) return "md";
  return "lg";
}

/** @returns {0 | 1} */
export function detectIsTouch() {
  if (typeof window === "undefined") return 0;
  if (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) {
    return 1;
  }
  if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) return 1;
  return 0;
}

/**
 * @param {number[]} values
 * @returns {number}
 */
export function medianInt(values) {
  if (!values || values.length === 0) return 0;
  var sorted = values.slice().sort(function (a, b) { return a - b; });
  var mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return Math.floor(sorted[mid]);
  return Math.floor((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * @param {number[]} values
 * @param {number} pct
 * @returns {number}
 */
export function percentileInt(values, pct) {
  if (!values || values.length === 0) return 0;
  var sorted = values.slice().sort(function (a, b) { return a - b; });
  var rank = Math.ceil((pct / 100) * sorted.length) - 1;
  rank = Math.max(0, Math.min(sorted.length - 1, rank));
  return Math.floor(sorted[rank]);
}

/**
 * @typedef {Object} PerfMonitorOptions
 * @property {(hz: number) => void} [onDisplayHzReady]
 */

export class PerfMonitor {
  /**
   * @param {PerfMonitorOptions} [options]
   */
  constructor(options) {
    options = options || {};
    this._onDisplayHzReady = typeof options.onDisplayHzReady === "function"
      ? options.onDisplayHzReady
      : null;
    this.reset();
  }

  reset() {
    this._lastNow = null;
    this._rollSince = null;
    this._rollFrames = 0;
    this._rollingFps = 60;
    this._lowEpisodeStart = null;
    this._lowEpisodeReported = false;
    this._episodeFrameMs = [];
    this._lowEpisodeFpsSum = 0;
    this._lowEpisodeFpsSamples = 0;
    this._lastLowFpsEventAt = null;
    this._hzSamples = [];
    this._hzSampleSince = null;
    this._hzEstimateDone = false;
  }

  /**
   * @param {number} now
   * @param {string} perfLoop
   * @param {{ frameMs?: number, bodies?: number }} [ctx]
   * @returns {Record<string, string | number> | null}
   */
  tick(now, perfLoop, ctx) {
    ctx = ctx || {};
    if (!isFinite(now)) return null;

    var frameMs = ctx.frameMs;
    if (frameMs == null) {
      frameMs = this._lastNow != null ? now - this._lastNow : 0;
    }
    if (!isFinite(frameMs) || frameMs < 0) frameMs = 0;
    frameMs = Math.min(PERF_MAX_FRAME_MS, frameMs);
    this._lastNow = now;

    this._sampleDisplayHz(now, frameMs);
    this._updateRollingFps(now);

    return this._maybeEmitLowFps(now, perfLoop, frameMs, ctx.bodies);
  }

  /**
   * @param {number} now
   * @param {number} frameMs
   */
  _sampleDisplayHz(now, frameMs) {
    if (this._hzEstimateDone) return;
    if (this._hzSampleSince == null) this._hzSampleSince = now;
    if (frameMs > 0 && frameMs < 200) {
      this._hzSamples.push(Math.round(1000 / frameMs));
    }
    if (now - this._hzSampleSince < DISPLAY_HZ_SAMPLE_MS) return;
    this._hzEstimateDone = true;
    var hz = medianInt(this._hzSamples);
    if (this._onDisplayHzReady) this._onDisplayHzReady(hz);
  }

  /** @param {number} now */
  _updateRollingFps(now) {
    if (this._rollSince == null) {
      this._rollSince = now;
      this._rollFrames = 0;
    }
    this._rollFrames += 1;
    var elapsed = now - this._rollSince;
    if (elapsed < 16) return;
    this._rollingFps = Math.round((this._rollFrames / elapsed) * 1000);
    if (elapsed >= PERF_ROLLING_WINDOW_MS) {
      this._rollSince = now;
      this._rollFrames = 0;
    }
  }

  /**
   * @param {number} now
   * @param {string} perfLoop
   * @param {number} frameMs
   * @param {number} [bodies]
   * @returns {Record<string, string | number> | null}
   */
  _maybeEmitLowFps(now, perfLoop, frameMs, bodies) {
    var below = this._rollingFps < PERF_LOW_FPS_THRESHOLD;
    if (below) {
      if (this._lowEpisodeStart == null) {
        this._lowEpisodeStart = now;
        this._lowEpisodeReported = false;
        this._episodeFrameMs = [];
        this._lowEpisodeFpsSum = 0;
        this._lowEpisodeFpsSamples = 0;
      }
      this._episodeFrameMs.push(frameMs);
      this._lowEpisodeFpsSum += this._rollingFps;
      this._lowEpisodeFpsSamples += 1;

      var duration = now - this._lowEpisodeStart;
      var cooledDown = this._lastLowFpsEventAt == null
        || now - this._lastLowFpsEventAt >= PERF_LOW_FPS_COOLDOWN_MS;
      if (!this._lowEpisodeReported
          && duration >= PERF_LOW_FPS_MIN_DURATION_MS
          && cooledDown) {
        this._lowEpisodeReported = true;
        this._lastLowFpsEventAt = now;
        var meanFps = this._lowEpisodeFpsSamples > 0
          ? Math.round(this._lowEpisodeFpsSum / this._lowEpisodeFpsSamples)
          : this._rollingFps;
        return {
          perf_loop: perfLoop || "merge",
          raf_fps: meanFps,
          duration_ms: Math.floor(duration),
          bodies: Math.max(0, Math.floor(Number(bodies) || 0)),
          frame_ms_p95: percentileInt(this._episodeFrameMs, 95),
        };
      }
      return null;
    }

    this._lowEpisodeStart = null;
    this._lowEpisodeReported = false;
    this._episodeFrameMs = [];
    this._lowEpisodeFpsSum = 0;
    this._lowEpisodeFpsSamples = 0;
    return null;
  }
}

/**
 * @param {PerfMonitorOptions} [options]
 * @returns {PerfMonitor}
 */
export function createPerfMonitor(options) {
  return new PerfMonitor(options);
}